/**
 * Formulario para crear nuevas compras - VERSIÓN FINAL CORREGIDA
 * 
 * Permite registrar nuevas órdenes de compra a proveedores con búsqueda inteligente
 * 
 * @module pages/compras/CompraForm
 * @requires react, react-router-dom, ../../services/compras.service
 */

import React, { useState, useEffect, useRef } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { toast } from 'react-toastify';
import { useAuth } from '../../contexts/AuthContext';
import { useSafeUpdateFormGuard } from '../../hooks/useSafeUpdateFormGuard';
import { BILLETERA_VIRTUAL_LABEL } from '../../utils/metodoPagoLabels';
import { parseCompraNumber, buildCompraResumen, roundMoney } from '../../utils/parseCompraNumber';

// Servicios
import comprasService from '../../services/compras.service';
import productosService from '../../services/productos.service';
import stockSucursalService from '../../services/stock-sucursal.service';
import proveedoresService from '../../services/proveedores.service';

// Componentes
import Card from '../../components/common/Card';
import Button from '../../components/common/Button';
import Spinner from '../../components/common/Spinner';
import CompraProductoRapidoModal from '../../components/compras/CompraProductoRapidoModal';

// Iconos
import { 
  FaShoppingCart, FaArrowLeft, FaSave, FaTimes, 
  FaPlus, FaTrash, FaUserTie, FaBoxOpen, FaStore,
  FaSearch, FaExclamationTriangle, FaBarcode
} from 'react-icons/fa';

/**
 * Componente de formulario mejorado para crear compras
 */
const CompraForm = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { sucursalSeleccionada } = useAuth();
  
  // Referencias
  const busquedaSeqRef = useRef(0);
  const proveedorPreselect = String(searchParams.get('proveedorId') || '').trim();
  
  // Estado principal
  const [formData, setFormData] = useState({
    proveedor_id: proveedorPreselect,
    notas: ''
  });
  
  const [detalles, setDetalles] = useState([
    { producto_id: '', cantidad: '', precio_unitario: '', subtotal: 0, producto_info: null }
  ]);
  
  const [proveedores, setProveedores] = useState([]);
  const [productos, setProductos] = useState([]);
  const [loadingProveedores, setLoadingProveedores] = useState(true);
  const [loadingProductos, setLoadingProductos] = useState(true);
  const [guardando, setGuardando] = useState(false);
  const [total, setTotal] = useState(0);
  const [fieldErrors, setFieldErrors] = useState({});
  const [lineErrors, setLineErrors] = useState({});
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [confirmResumen, setConfirmResumen] = useState(null);
  const [abnormalAck, setAbnormalAck] = useState(false);
  // Pago: '' | pendiente = crédito/sin egreso; caja | externo = paga ahora
  const [pagoOrigen, setPagoOrigen] = useState('pendiente');
  const [pagoMedio, setPagoMedio] = useState('efectivo');
  const [permitirNegativo, setPermitirNegativo] = useState(false);
  
  // Estados para búsqueda de productos
  const [busquedaProductos, setBusquedaProductos] = useState('');
  const [productosSugeridos, setProductosSugeridos] = useState([]);
  const [mostrandoSugerencias, setMostrandoSugerencias] = useState(false);
  const [indiceSugerenciaActiva, setIndiceSugerenciaActiva] = useState(-1);
  const [filaActivaBusqueda, setFilaActivaBusqueda] = useState(-1);

  const [quickModal, setQuickModal] = useState({
    open: false,
    rowIndex: 0,
    nombreHint: '',
    precioHint: ''
  });

  const compraDirty = detalles.some((d) => Boolean(d.producto_id))
    || Boolean(formData.proveedor_id)
    || Boolean(String(formData.notas || '').trim());
  useSafeUpdateFormGuard('compra', compraDirty);

  /**
   * Carga datos iniciales
   */
  useEffect(() => {
    const cargarDatos = async () => {
      try {
        const dataProveedores = await proveedoresService.obtenerTodos();
        const list = Array.isArray(dataProveedores) ? dataProveedores : [];
        setProveedores(list);
        if (proveedorPreselect && list.some((p) => p.id === proveedorPreselect)) {
          setFormData((prev) => ({ ...prev, proveedor_id: proveedorPreselect }));
        }
        setLoadingProveedores(false);
        
        const dataProductos = await productosService.obtenerTodos();
        setProductos(dataProductos);
        setLoadingProductos(false);
      } catch (error) {
        console.error('Error al cargar datos iniciales:', error);
        setLoadingProveedores(false);
        setLoadingProductos(false);
        toast.error('Error al cargar datos necesarios');
      }
    };
    
    cargarDatos();
  }, [proveedorPreselect]);

  /**
   * Stock canónico por sucursal activa (mismo origen que tras seleccionar).
   */
  const resolverStockSucursal = async (productoId) => {
    if (!sucursalSeleccionada?.id || !productoId) {
      return { ok: false, cantidad: null, error: 'Sucursal no disponible' };
    }
    try {
      const stockSucursales = await stockSucursalService.obtenerStockPorProducto(productoId);
      const stockDeSucursal = Array.isArray(stockSucursales)
        ? stockSucursales.find((s) => s.sucursal_id === sucursalSeleccionada.id)
        : null;
      if (stockDeSucursal && typeof stockDeSucursal.cantidad !== 'undefined') {
        return { ok: true, cantidad: parseFloat(stockDeSucursal.cantidad) || 0 };
      }
    } catch (e) {
      console.warn('No se pudo cargar stock por sucursal:', e.message);
      return { ok: false, cantidad: null, error: e.message || 'Stock no disponible' };
    }
    return { ok: true, cantidad: 0 };
  };

  /**
   * Búsqueda inteligente de productos (stock = canónico sucursal).
   */
  const buscarProductos = (termino) => {
    const seq = busquedaSeqRef.current + 1;
    busquedaSeqRef.current = seq;

    if (!termino || termino.length < 2) {
      setProductosSugeridos([]);
      setMostrandoSugerencias(false);
      return;
    }

    const terminoLower = termino.toLowerCase();
    const resultados = productos
      .filter(producto =>
        producto.activo !== false &&
        (String(producto.nombre || '').toLowerCase().includes(terminoLower) ||
         String(producto.codigo || '').toLowerCase().includes(terminoLower) ||
         String(producto.codigo_barras || '').toLowerCase().includes(terminoLower))
      )
      .sort((a, b) => {
        const aCodigoExacto = String(a.codigo || '').toLowerCase() === terminoLower;
        const bCodigoExacto = String(b.codigo || '').toLowerCase() === terminoLower;
        if (aCodigoExacto && !bCodigoExacto) return -1;
        if (bCodigoExacto && !aCodigoExacto) return 1;

        const aNombreEmpieza = String(a.nombre || '').toLowerCase().startsWith(terminoLower);
        const bNombreEmpieza = String(b.nombre || '').toLowerCase().startsWith(terminoLower);
        if (aNombreEmpieza && !bNombreEmpieza) return -1;
        if (bNombreEmpieza && !aNombreEmpieza) return 1;

        return String(a.nombre || '').localeCompare(String(b.nombre || ''));
      })
      .slice(0, 20);

    const base = resultados.map((producto) => ({
      ...producto,
      stock_actual: null,
      stock_status: 'loading',
      stock_sucursal_id: sucursalSeleccionada?.id || null,
      stock_sucursal_nombre: sucursalSeleccionada?.nombre || null,
    }));

    setProductosSugeridos(base);
    setMostrandoSugerencias(true);
    setIndiceSugerenciaActiva(-1);

    Promise.allSettled(
      resultados.map(async (producto) => ({
        id: producto.id,
        stock: await resolverStockSucursal(producto.id)
      }))
    ).then((settled) => {
      if (busquedaSeqRef.current !== seq) return;
      const stockPorProducto = new Map();
      settled.forEach((result) => {
        if (result.status === 'fulfilled') {
          stockPorProducto.set(result.value.id, result.value.stock);
        }
      });
      setProductosSugeridos((prev) => prev.map((producto) => {
        const stock = stockPorProducto.get(producto.id);
        if (!stock || stock.ok !== true) {
          return {
            ...producto,
            stock_actual: null,
            stock_status: 'unavailable'
          };
        }
        return {
          ...producto,
          stock_actual: stock.cantidad,
          stock_status: 'ok'
        };
      }));
    });
  };

  /**
   * Maneja cambios en el campo de búsqueda
   */
  const handleBusquedaChange = (e, index) => {
    const valor = e.target.value;
    setBusquedaProductos(valor);
    setFilaActivaBusqueda(index);
    buscarProductos(valor);
  };

  /**
   * Maneja teclas en el campo de búsqueda
   */
  const handleBusquedaKeyDown = (e, index) => {
    if (!mostrandoSugerencias) return;

    switch (e.key) {
      case 'ArrowDown':
        e.preventDefault();
        setIndiceSugerenciaActiva(prev => 
          prev < productosSugeridos.length - 1 ? prev + 1 : 0
        );
        break;
      case 'ArrowUp':
        e.preventDefault();
        setIndiceSugerenciaActiva(prev => 
          prev > 0 ? prev - 1 : productosSugeridos.length - 1
        );
        break;
      case 'Enter':
        e.preventDefault();
        if (indiceSugerenciaActiva >= 0) {
          seleccionarProducto(productosSugeridos[indiceSugerenciaActiva], index);
        }
        break;
      case 'Escape':
        setMostrandoSugerencias(false);
        setIndiceSugerenciaActiva(-1);
        break;
      default:
        break;
    }
  };

  /**
   * Selecciona un producto de las sugerencias
   */
  const seleccionarProducto = async (producto, index) => {
    const nuevosDetalles = [...detalles];
    nuevosDetalles[index] = {
      ...nuevosDetalles[index],
      producto_id: producto.id,
      precio_unitario: producto.precio_costo || 0,
      producto_info: {
        id: producto.id,
        codigo: producto.codigo,
        nombre: producto.nombre,
        precio_costo: producto.precio_costo,
        stock_actual: 0
      }
    };

    const q = parseCompraNumber(nuevosDetalles[index].cantidad, { role: 'cantidad' });
    const p = parseCompraNumber(nuevosDetalles[index].precio_unitario, { role: 'precio' });
    nuevosDetalles[index].subtotal = q.ok && p.ok ? roundMoney(q.value * p.value) : 0;

    // Stock canónico: mismo resolver que el autocomplete
    const stockCanonico =
      producto.stock_status === 'ok' &&
      typeof producto.stock_actual === 'number' &&
      producto.stock_sucursal_id === sucursalSeleccionada?.id
        ? { ok: true, cantidad: producto.stock_actual }
        : await resolverStockSucursal(producto.id);
    nuevosDetalles[index].producto_info.stock_actual = stockCanonico.ok ? stockCanonico.cantidad : null;
    nuevosDetalles[index].producto_info.stock_no_disponible = stockCanonico.ok !== true;

    setDetalles(nuevosDetalles);
    setBusquedaProductos('');
    setMostrandoSugerencias(false);
    setProductosSugeridos([]);
    calcularTotal(nuevosDetalles);

    // Enfocar el campo de cantidad
    setTimeout(() => {
      const cantidadInput = document.getElementById(`cantidad-${index}`);
      if (cantidadInput) cantidadInput.focus();
    }, 100);
  };

  /**
   * Limpia la selección de producto
   */
  const limpiarProductoSeleccionado = (index) => {
    const nuevosDetalles = [...detalles];
    nuevosDetalles[index] = {
      producto_id: '',
      cantidad: nuevosDetalles[index].cantidad, // Mantener cantidad si ya tenía
      precio_unitario: '',
      subtotal: 0,
      producto_info: null
    };
    setDetalles(nuevosDetalles);
    calcularTotal(nuevosDetalles);
    setBusquedaProductos('');
    setMostrandoSugerencias(false);
  };

  const abrirAltaRapida = (index) => {
    if (!formData.proveedor_id) {
      toast.warning('Elegí un proveedor primero y seguimos.');
      return;
    }
    const hintNombre =
      filaActivaBusqueda === index ? busquedaProductos.trim() : '';
    const precioLinea = detalles[index]?.precio_unitario;
    setQuickModal({
      open: true,
      rowIndex: index,
      nombreHint: hintNombre,
      precioHint: precioLinea !== '' && precioLinea != null ? String(precioLinea) : ''
    });
  };

  const handleProductoRapidoCreado = async (payload) => {
    try {
      const resultado = await productosService.crear(payload);
      const raw = resultado?.data ?? resultado;
      const newId = raw?.id;
      if (!newId) {
        throw new Error('El servidor no devolvió el id del producto');
      }
      const lista = await productosService.obtenerTodos();
      setProductos(lista);
      const nuevo = lista.find((p) => p.id === newId) || { ...payload, id: newId };
      await seleccionarProducto(nuevo, quickModal.rowIndex);
      setQuickModal((s) => ({ ...s, open: false }));
      toast.success(`Listo: «${payload.nombre}» ya está en esta compra.`, { autoClose: 4000 });
    } catch (err) {
      toast.error(err?.response?.data?.message || err?.message || 'No se pudo crear el producto');
      throw err;
    }
  };

  /**
   * Maneja cambios en los datos generales de la compra
   */
  const handleCompraChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };
  
  /**
   * Maneja cambios en los detalles de productos
   */
  const handleDetalleChange = (e, index) => {
    const { name, value } = e.target;
    
    const nuevosDetalles = [...detalles];
    nuevosDetalles[index] = {
      ...nuevosDetalles[index],
      [name]: value
    };
    
    // Si cambió cantidad o precio, recalcular subtotal (parser canónico)
    if (name === 'cantidad' || name === 'precio_unitario') {
      const q = parseCompraNumber(
        name === 'cantidad' ? value : nuevosDetalles[index].cantidad,
        { role: 'cantidad' }
      );
      const p = parseCompraNumber(
        name === 'precio_unitario' ? value : nuevosDetalles[index].precio_unitario,
        { role: 'precio' }
      );
      nuevosDetalles[index].subtotal =
        q.ok && p.ok ? roundMoney(q.value * p.value) : 0;
      setLineErrors((prev) => ({
        ...prev,
        [index]: {
          cantidad: name === 'cantidad' && value !== '' && !q.ok ? q.message : undefined,
          precio: name === 'precio_unitario' && value !== '' && !p.ok ? p.message : undefined,
        },
      }));
    }
    
    setDetalles(nuevosDetalles);
    calcularTotal(nuevosDetalles);
  };
  
  /**
   * Calcula el total de la compra
   */
  const calcularTotal = (items) => {
    const nuevoTotal = items.reduce(
      (sum, item) => sum + (parseFloat(item.subtotal) || 0),
      0
    );
    setTotal(roundMoney(nuevoTotal));
  };
  
  /**
   * Agrega una nueva línea de detalle
   */
  const agregarDetalle = () => {
    setDetalles([
      ...detalles,
      { producto_id: '', cantidad: '', precio_unitario: '', subtotal: 0, producto_info: null }
    ]);
  };
  
  /**
   * Elimina una línea de detalle
   */
  const eliminarDetalle = (index) => {
    if (detalles.length === 1) {
      toast.warning('Debe mantener al menos un producto');
      return;
    }
    
    const nuevosDetalles = detalles.filter((_, i) => i !== index);
    setDetalles(nuevosDetalles);
    calcularTotal(nuevosDetalles);
  };
  
  /**
   * Formatea un valor numérico a 2 decimales
   */
  const formatearNumero = (valor) => {
    return parseFloat(valor || 0).toFixed(2);
  };
  
  const validarFormulario = () => {
    const errors = {};
    const lErr = {};

    if (!sucursalSeleccionada?.id) {
      errors.sucursal = 'Seleccioná una sucursal.';
      toast.error('Seleccioná una sucursal en el encabezado antes de guardar la compra.');
    }

    if (!formData.proveedor_id) {
      errors.proveedor = 'Seleccioná un proveedor.';
      toast.error('Seleccioná un proveedor.');
    }

    const resumen = buildCompraResumen(detalles);
    if (!resumen.ok) {
      if (resumen.empty) {
        errors.productos = 'Agregá al menos un producto.';
        toast.error('Agregá al menos un producto.');
      } else {
        const idx = resumen.lineIndex;
        const msg =
          (!resumen.qty?.ok && resumen.qty?.message) ||
          (!resumen.price?.ok && resumen.price?.message) ||
          'Revisá esta línea antes de guardar.';
        lErr[idx] = {
          cantidad: resumen.qty && !resumen.qty.ok ? resumen.qty.message : undefined,
          precio: resumen.price && !resumen.price.ok ? resumen.price.message : undefined,
        };
        errors.productos = `Fila #${idx + 1}: ${msg}`;
        toast.error(errors.productos);
      }
    }

    if (!pagoOrigen || !['pendiente', 'caja', 'externo'].includes(pagoOrigen)) {
      errors.pago = 'Elegí si la compra queda pendiente o se paga ahora.';
      toast.error(errors.pago);
    }

    if (pagoOrigen === 'caja' && !pagoMedio) {
      errors.pago = 'Elegí el medio de pago de caja (efectivo, transferencia, etc.).';
      toast.error(errors.pago);
    }

    setLineErrors(lErr);
    setFieldErrors(errors);
    if (Object.keys(errors).length) return null;
    return resumen;
  };

  const ejecutarCreacion = async (resumen, { permitirNeg = permitirNegativo } = {}) => {
    setGuardando(true);
    const compra = {
      proveedor_id: formData.proveedor_id,
      sucursal_id: sucursalSeleccionada.id,
      sucursal_nombre: sucursalSeleccionada.nombre,
      notas: formData.notas,
      estado_pago: 'pendiente',
      total: resumen.total,
    };
    if (pagoOrigen === 'caja' || pagoOrigen === 'externo') {
      compra.pago = {
        origen: pagoOrigen,
        medio_pago: pagoMedio,
        permitir_negativo: permitirNeg,
      };
    }
    const detallesLimpios = resumen.lines.map((l) => ({
      producto_id: l.producto_id,
      cantidad: l.cantidad,
      precio_unitario: l.precio_unitario,
      subtotal: l.subtotal,
    }));

    try {
      const response = await comprasService.crear(compra, detallesLimpios);
      toast.success('Compra registrada correctamente');
      const compraId = response.data?.id || response.id || response;
      navigate(`/compras/${compraId}`);
    } catch (error) {
      console.error('Error al crear compra:', error);
      const data = error.response?.data;
      if (data?.requiere_confirmacion && pagoOrigen === 'caja' && !permitirNeg) {
        const saldoActual = parseFloat(data.saldo_actual ?? data.saldo_medio ?? 0).toFixed(2);
        const mensaje = `${data.message || 'Saldo insuficiente'}.\n\nSaldo: $${saldoActual}\nTotal: $${resumen.total}\n\n¿Continuar con saldo negativo?`;
        if (window.confirm(mensaje)) {
          setPermitirNegativo(true);
          await ejecutarCreacion(resumen, { permitirNeg: true });
          return;
        }
        toast.info('Operación cancelada. No se registró la compra.');
        return;
      }
      toast.error(data?.message || error.message || 'Error al registrar la compra');
    } finally {
      setGuardando(false);
    }
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (guardando) return;
    const resumen = validarFormulario();
    if (!resumen) return;
    setAbnormalAck(false);
    setConfirmResumen(resumen);
    setConfirmOpen(true);
  };

  const confirmarGuardado = async () => {
    if (!confirmResumen || guardando) return;
    const abnormal =
      confirmResumen.unidades >= 100 || confirmResumen.total >= 500000;
    if (abnormal && !abnormalAck) {
      toast.warning('Confirmá explícitamente los valores elevados antes de guardar.');
      return;
    }
    setConfirmOpen(false);
    await ejecutarCreacion(confirmResumen);
  };
  
  // Si está cargando, mostrar spinner
  if (loadingProveedores || loadingProductos) {
    return (
      <div className="flex justify-center items-center h-64">
        <Spinner size="lg" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <h1 className="text-2xl font-bold text-e0e3-foreground">Nueva Compra</h1>
        
        <Button
          color="secondary"
          onClick={() => navigate('/compras')}
          icon={<FaArrowLeft />}
        >
          Volver
        </Button>
      </div>
      
      <form onSubmit={handleSubmit} className="w-full">
        <div className="flex flex-col lg:flex-row gap-6">
          {/* Panel izquierdo: Datos generales - Ancho fijo */}
          <div className="w-full lg:w-80 flex-shrink-0">
            <Card
              title="Información de la Compra"
              icon={<FaShoppingCart />}
            >
              <div className="space-y-4">
                {/* Mostrar sucursal actual */}
                <div className="rounded-md p-3 flex items-center border border-blue-500/30 bg-blue-500/10">
                  <FaStore className="text-blue-400 mr-2" />
                  <div>
                    <p className="text-sm font-medium text-blue-300">Sucursal:</p>
                    <p className="text-sm text-blue-200">
                      {sucursalSeleccionada?.nombre || 'No seleccionada'}
                    </p>
                  </div>
                </div>
                
                {/* Selección de proveedor */}
                <div>
                  <label className="block text-sm font-medium text-e0e3-muted mb-1">
                    Proveedor *
                  </label>
                  {!loadingProveedores && proveedores.length === 0 && (
                    <div className="mb-3 rounded-lg border border-amber-400/40 bg-amber-500/10 p-3 text-sm text-amber-100">
                      <p className="font-medium">Todavía no tenés proveedores registrados.</p>
                      <p className="mt-1 text-amber-100/90">
                        Creá uno para comenzar a registrar compras.
                      </p>
                      <Link
                        to="/proveedores/nuevo?returnTo=/compras/nueva"
                        className="mt-2 inline-flex font-semibold underline"
                      >
                        Crear proveedor
                      </Link>
                    </div>
                  )}
                  {fieldErrors.sucursal && (
                    <p className="mb-2 text-sm text-red-400" role="alert">{fieldErrors.sucursal}</p>
                  )}
                  <div className="relative">
                    <FaUserTie className="absolute left-3 top-1/2 transform -translate-y-1/2 text-e0e3-muted/70" />
                    <select
                      name="proveedor_id"
                      value={formData.proveedor_id}
                      onChange={(e) => {
                        handleCompraChange(e);
                        setFieldErrors((prev) => ({ ...prev, proveedor: undefined }));
                      }}
                      className={`nexo-field pl-10 pr-3 sm:text-sm ${fieldErrors.proveedor ? 'ring-1 ring-red-400' : ''}`}
                      required
                    >
                      <option value="">Selecciona un proveedor</option>
                      {proveedores.map(proveedor => (
                        <option key={proveedor.id} value={proveedor.id}>
                          {proveedor.nombre}
                        </option>
                      ))}
                    </select>
                  </div>
                  {fieldErrors.proveedor && (
                    <p className="mt-1 text-sm text-red-400" role="alert">{fieldErrors.proveedor}</p>
                  )}
                </div>
                
                {/* Notas */}
                <div>
                  <label className="block text-sm font-medium text-e0e3-muted mb-1">
                    Notas
                  </label>
                  <textarea
                    name="notas"
                    value={formData.notas}
                    onChange={handleCompraChange}
                    rows={3}
                    className="nexo-field sm:text-sm"
                    placeholder="Observaciones o detalles adicionales..."
                  />
                </div>
                
                {/* Resumen */}
                <div className="border-t pt-4 mt-4">
                  <div className="flex justify-between font-medium text-lg">
                    <span>Total:</span>
                    <span className="text-indigo-600">${formatearNumero(total)}</span>
                  </div>
                </div>
              </div>
            </Card>
          </div>
          
          {/* Panel derecho: Detalles de productos - Ocupa todo el espacio restante */}
          <div className="flex-1 min-w-0">
            <Card
              title="Productos"
              icon={<FaBoxOpen />}
            >
              {fieldErrors.productos && (
                <p className="mb-3 text-sm text-red-400" role="alert">{fieldErrors.productos}</p>
              )}
              <div>
                {/* Encabezados de tabla con ancho completo */}
                <div className="w-full">
                  <div className="flex gap-4 mb-3 px-1">
                    <div className="flex-1">
                      <label className="text-xs font-medium text-e0e3-muted">Producto *</label>
                    </div>
                    <div className="w-24 text-center flex-shrink-0">
                      <label className="text-xs font-medium text-e0e3-muted">Cantidad *</label>
                    </div>
                    <div className="w-32 text-center flex-shrink-0">
                      <label className="text-xs font-medium text-e0e3-muted">Precio Unit. *</label>
                    </div>
                    <div className="w-32 text-center flex-shrink-0">
                      <label className="text-xs font-medium text-e0e3-muted">Subtotal</label>
                    </div>
                    <div className="w-10 flex-shrink-0"></div>
                  </div>
                  
                  {/* Lista de productos */}
                  <div className="space-y-2 w-full">
                    {detalles.map((detalle, index) => (
                      <div key={index} className="bg-white/[0.04] border border-white/10 rounded-lg p-3 w-full">
                        <div className="flex gap-4 items-start">
                          
                          {/* Búsqueda de producto - Ocupa todo el espacio disponible */}
                          <div className="flex-1 min-w-0 relative">
                            {detalle.producto_info ? (
                              // Producto seleccionado
                              <div className="border border-emerald-500/30 bg-emerald-500/10 rounded-md p-3">
                                <div className="flex justify-between items-start">
                                  <div className="flex-1 min-w-0 pr-2">
                                    <p className="font-medium text-base text-e0e3-foreground">
                                      {detalle.producto_info.nombre}
                                    </p>
                                    <div className="flex items-center text-sm text-e0e3-muted mt-1">
                                      <FaBarcode className="mr-1" size={12} />
                                      {detalle.producto_info.codigo}
                                      <span className="mx-2">•</span>
                                      <span className="text-green-600">
                                        {detalle.producto_info.stock_no_disponible
                                          ? 'Stock no disponible'
                                          : `Stock disponible: ${detalle.producto_info.stock_actual ?? 0}`}
                                      </span>
                                    </div>
                                  </div>
                                  <button
                                    type="button"
                                    onClick={() => limpiarProductoSeleccionado(index)}
                                    className="text-red-400 hover:text-red-300 p-1 hover:bg-red-500/10 rounded flex-shrink-0"
                                  >
                                    <FaTimes size={14} />
                                  </button>
                                </div>
                              </div>
                            ) : (
                              // Búsqueda de producto
                              <div className="relative">
                                <div className="relative">
                                  <FaSearch className="absolute left-3 top-1/2 transform -translate-y-1/2 text-e0e3-muted/70" size={14} />
                                  <input
                                    type="text"
                                    placeholder="Buscar producto por nombre o código..."
                                    value={filaActivaBusqueda === index ? busquedaProductos : ''}
                                    onChange={(e) => handleBusquedaChange(e, index)}
                                    onKeyDown={(e) => handleBusquedaKeyDown(e, index)}
                                    onFocus={() => setFilaActivaBusqueda(index)}
                                    className="nexo-field pl-9 pr-3"
                                  />
                                </div>
                                
                                {/* Sugerencias de productos */}
                                {mostrandoSugerencias && filaActivaBusqueda === index && productosSugeridos.length > 0 && (
                                  <div className="absolute z-20 mt-1 w-full bg-e0e3-surface/95 shadow-lg rounded-md border border-white/10 max-h-96 overflow-y-auto">
                                    {productosSugeridos.map((producto, suggestionIndex) => (
                                      <div
                                        key={producto.id}
                                        className={`px-3 py-2 cursor-pointer hover:bg-white/[0.06] border-b border-white/10 last:border-b-0 ${
                                          suggestionIndex === indiceSugerenciaActiva ? 'bg-e0e3-accent/10' : ''
                                        }`}
                                        onClick={() => seleccionarProducto(producto, index)}
                                      >
                                        <div className="flex justify-between items-start gap-3">
                                          <div className="flex-1 min-w-0">
                                            <p className="font-medium text-sm text-e0e3-foreground truncate">
                                              {producto.nombre}
                                            </p>
                                            <div className="flex items-center gap-2 text-xs text-e0e3-muted mt-0.5">
                                              <span className="font-mono bg-white/[0.08] px-1.5 py-0.5 rounded">{producto.codigo}</span>
                                              <span className="text-blue-600 font-semibold">${producto.precio_costo?.toLocaleString() || 0}</span>
                                            </div>
                                          </div>
                                          <div className="text-right shrink-0">
                                            <span className={`text-xs px-2 py-0.5 rounded-full ${
                                              producto.stock_status === 'loading'
                                                ? 'bg-blue-500/15 text-blue-300'
                                                : (producto.stock_actual || 0) > 0
                                                  ? 'bg-emerald-500/15 text-emerald-300'
                                                  : 'bg-red-500/15 text-red-300'
                                            }`}>
                                              {producto.stock_status === 'loading'
                                                ? 'Cargando stock...'
                                                : producto.stock_status === 'unavailable'
                                                  ? 'Stock no disponible'
                                                  : `Stock disponible: ${producto.stock_actual || 0}`}
                                            </span>
                                            {producto.stock_sucursal_nombre ? (
                                              <p className="mt-0.5 text-[10px] text-e0e3-muted">
                                                Sucursal: {producto.stock_sucursal_nombre}
                                              </p>
                                            ) : null}
                                          </div>
                                        </div>
                                      </div>
                                    ))}
                                  </div>
                                )}
                                <div className="mt-2 flex flex-wrap items-center gap-2">
                                  <button
                                    type="button"
                                    onClick={() => abrirAltaRapida(index)}
                                    className="inline-flex items-center rounded-lg bg-gradient-to-r from-indigo-600 to-violet-600 px-3 py-1.5 text-xs font-semibold text-white shadow-sm transition hover:from-indigo-700 hover:to-violet-700"
                                  >
                                    <FaPlus className="mr-1 h-3 w-3" /> Alta rápida
                                  </button>
                                  {mostrandoSugerencias &&
                                    filaActivaBusqueda === index &&
                                    busquedaProductos.trim().length >= 2 &&
                                    productosSugeridos.length === 0 && (
                                      <span className="rounded-lg bg-amber-500/15 px-2 py-1 text-xs font-medium text-amber-300 ring-1 ring-amber-500/30">
                                        Sin coincidencias — podés crear el producto con el botón
                                      </span>
                                    )}
                                </div>
                              </div>
                            )}
                          </div>
                          
                          {/* Cantidad — text + parser canónico (evita 1.100 ambiguo) */}
                          <div className="w-28 flex-shrink-0">
                            <input
                              id={`cantidad-${index}`}
                              type="text"
                              inputMode="decimal"
                              name="cantidad"
                              value={detalle.cantidad}
                              onChange={(e) => handleDetalleChange(e, index)}
                              className={`nexo-field py-2.5 text-center text-sm ${lineErrors[index]?.cantidad ? 'ring-1 ring-red-400' : ''}`}
                              required
                              disabled={!detalle.producto_id}
                              placeholder="ej. 1 o 1,5"
                              autoComplete="off"
                            />
                            {lineErrors[index]?.cantidad && (
                              <p className="mt-0.5 text-[10px] text-red-400" role="alert">
                                {lineErrors[index].cantidad}
                              </p>
                            )}
                          </div>
                          
                          {/* Precio Unitario */}
                          <div className="w-32 flex-shrink-0">
                            <input
                              type="text"
                              inputMode="decimal"
                              name="precio_unitario"
                              value={detalle.precio_unitario}
                              onChange={(e) => handleDetalleChange(e, index)}
                              className={`nexo-field py-2.5 text-center text-sm ${lineErrors[index]?.precio ? 'ring-1 ring-red-400' : ''}`}
                              required
                              disabled={!detalle.producto_id}
                              placeholder="0,00"
                              autoComplete="off"
                            />
                            {lineErrors[index]?.precio && (
                              <p className="mt-0.5 text-[10px] text-red-400" role="alert">
                                {lineErrors[index].precio}
                              </p>
                            )}
                          </div>
                          
                          {/* Subtotal */}
                          <div className="w-32 flex-shrink-0">
                            <div className="block w-full px-3 py-2.5 bg-white/[0.06] border border-white/10 rounded-md text-sm font-medium text-center text-e0e3-foreground">
                              ${formatearNumero(detalle.subtotal)}
                            </div>
                          </div>
                          
                          {/* Botón eliminar */}
                          <div className="w-10 flex-shrink-0 flex items-center justify-center">
                            <button
                              type="button"
                              onClick={() => eliminarDetalle(index)}
                              disabled={detalles.length === 1}
                              className={`p-2 rounded-md transition-colors ${
                                detalles.length === 1 
                                  ? 'text-e0e3-muted/70 cursor-not-allowed' 
                                  : 'text-red-400 hover:text-red-300 hover:bg-red-500/10'
                              }`}
                              title="Eliminar producto"
                            >
                              <FaTrash size={14} />
                            </button>
                          </div>
                        </div>
                        
                        {/* Advertencia si no hay stock */}
                        {detalle.producto_info && !detalle.producto_info.stock_no_disponible && detalle.producto_info.stock_actual <= 0 && (
                          <div className="mt-1.5 mx-1 flex items-center text-amber-300 text-xs bg-amber-500/15 p-1.5 rounded border border-amber-500/30">
                            <FaExclamationTriangle size={11} className="mr-1" />
                            Este producto no tiene stock actual
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
                
                {/* Botón para agregar nuevo producto */}
                <div className="flex justify-center mt-4">
                  <button
                    type="button"
                    onClick={agregarDetalle}
                    className="px-4 py-2 border-2 border-dashed border-white/20 rounded-lg text-e0e3-muted hover:border-white/30 hover:text-e0e3-foreground transition-colors flex items-center"
                  >
                    <FaPlus className="mr-2" size={14} />
                    Agregar Producto
                  </button>
                </div>
              </div>
            </Card>
          </div>
        </div>
        
        {/* Botones de acción - Movidos abajo */}
        <div className="flex justify-end space-x-2 mt-6">
          {/* Selector de pago al finalizar compra */}
          <div className="flex-1 text-right mr-4">
            <div className="inline-flex gap-2 items-center">
              <fieldset className="text-left">
                <legend className="text-sm text-e0e3-muted mb-1">Forma de pago</legend>
                <div className="flex flex-col sm:flex-row gap-2 sm:gap-4 text-sm text-e0e3-foreground">
                  <label className="inline-flex items-center gap-2 cursor-pointer">
                    <input
                      type="radio"
                      name="forma_pago_compra"
                      value="pendiente"
                      checked={pagoOrigen === 'pendiente' || pagoOrigen === ''}
                      onChange={() => {
                        setPagoOrigen('pendiente');
                        setFieldErrors((prev) => ({ ...prev, pago: undefined }));
                      }}
                    />
                    Pendiente
                  </label>
                  <label className="inline-flex items-center gap-2 cursor-pointer">
                    <input
                      type="radio"
                      name="forma_pago_compra"
                      value="caja"
                      checked={pagoOrigen === 'caja'}
                      onChange={() => {
                        setPagoOrigen('caja');
                        setFieldErrors((prev) => ({ ...prev, pago: undefined }));
                      }}
                    />
                    Caja
                  </label>
                  <label className="inline-flex items-center gap-2 cursor-pointer">
                    <input
                      type="radio"
                      name="forma_pago_compra"
                      value="externo"
                      checked={pagoOrigen === 'externo'}
                      onChange={() => {
                        setPagoOrigen('externo');
                        setFieldErrors((prev) => ({ ...prev, pago: undefined }));
                      }}
                    />
                    Pago externo
                  </label>
                </div>
                {(pagoOrigen === 'pendiente' || pagoOrigen === '') && (
                  <p className="mt-1 max-w-md text-xs text-e0e3-muted">
                    La compra se guardará sin generar egreso ni deuda hasta registrar la factura.
                  </p>
                )}
                {fieldErrors.pago && (
                  <p className="mt-1 text-sm text-red-400" role="alert">{fieldErrors.pago}</p>
                )}
              </fieldset>
              {pagoOrigen === 'caja' && (
                <>
                  <select
                    value={pagoMedio}
                    onChange={e => setPagoMedio(e.target.value)}
                    className="nexo-field text-sm"
                  >
                    <option value="efectivo">Efectivo</option>
                    <option value="transferencia">Transferencia</option>
                    <option value="mercadopago">{BILLETERA_VIRTUAL_LABEL}</option>
                    <option value="tarjeta">Tarjeta</option>
                  </select>
                  <label className="inline-flex items-center gap-1 text-sm text-e0e3-muted">
                    <input type="checkbox" checked={permitirNegativo} onChange={e=> setPermitirNegativo(e.target.checked)} />
                    Permitir saldo negativo
                  </label>
                </>
              )}
            </div>
          </div>
          <Button
            type="button"
            color="secondary"
            onClick={() => navigate('/compras')}
            icon={<FaTimes />}
          >
            Cancelar
          </Button>
          
          <Button
            type="submit"
            color="primary"
            loading={guardando}
            disabled={guardando}
            icon={<FaSave />}
            onClick={() => {
              if (!sucursalSeleccionada?.id) {
                toast.error('Seleccioná una sucursal en el encabezado antes de guardar.');
              }
            }}
          >
            Revisar y guardar
          </Button>
        </div>
      </form>

      {confirmOpen && confirmResumen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4">
          <div className="max-h-[90vh] w-full max-w-lg overflow-auto rounded-xl border border-e0e3-border bg-e0e3-surface p-5 shadow-xl">
            <h2 className="text-lg font-bold text-e0e3-foreground">Confirmá la compra</h2>
            <p className="mt-1 text-sm text-e0e3-muted">
              Revisá cantidades y total antes de guardar. Este paso evita errores como 1100 unidades.
            </p>
            <dl className="mt-4 space-y-1 text-sm text-e0e3-foreground">
              <div className="flex justify-between gap-4">
                <dt className="text-e0e3-muted">Productos</dt>
                <dd>{confirmResumen.productos}</dd>
              </div>
              <div className="flex justify-between gap-4">
                <dt className="text-e0e3-muted">Unidades totales</dt>
                <dd className="font-semibold tabular-nums">{confirmResumen.unidades}</dd>
              </div>
              <div className="flex justify-between gap-4">
                <dt className="text-e0e3-muted">Total</dt>
                <dd className="font-semibold tabular-nums">${formatearNumero(confirmResumen.total)}</dd>
              </div>
              <div className="flex justify-between gap-4">
                <dt className="text-e0e3-muted">Forma de pago</dt>
                <dd>{pagoOrigen === 'pendiente' ? 'Pendiente' : pagoOrigen === 'caja' ? 'Caja' : 'Externo'}</dd>
              </div>
            </dl>
            <ul className="mt-4 max-h-48 space-y-2 overflow-auto text-sm">
              {confirmResumen.lines.map((l) => (
                <li key={l.index} className="rounded-lg border border-white/10 px-3 py-2">
                  <div className="font-medium">{l.nombre}</div>
                  <div className="text-e0e3-muted tabular-nums">
                    {l.cantidad} × ${formatearNumero(l.precio_unitario)} = ${formatearNumero(l.subtotal)}
                  </div>
                </li>
              ))}
            </ul>
            {(confirmResumen.unidades >= 100 || confirmResumen.total >= 500000) && (
              <label className="mt-4 flex items-start gap-2 rounded-lg border border-amber-400/40 bg-amber-500/10 p-3 text-sm text-amber-100">
                <input
                  type="checkbox"
                  className="mt-1"
                  checked={abnormalAck}
                  onChange={(e) => setAbnormalAck(e.target.checked)}
                />
                <span>
                  Vas a registrar <strong className="tabular-nums">{confirmResumen.unidades}</strong> unidades
                  por <strong className="tabular-nums">${formatearNumero(confirmResumen.total)}</strong>.
                  ¿Confirmás que estos valores son correctos?
                </span>
              </label>
            )}
            <div className="mt-5 flex justify-end gap-2">
              <Button type="button" color="secondary" disabled={guardando} onClick={() => setConfirmOpen(false)}>
                Volver
              </Button>
              <Button type="button" color="primary" loading={guardando} disabled={guardando} onClick={confirmarGuardado}>
                Confirmar compra
              </Button>
            </div>
          </div>
        </div>
      )}

      <CompraProductoRapidoModal
        open={quickModal.open}
        onClose={() => setQuickModal((s) => ({ ...s, open: false }))}
        onCreated={handleProductoRapidoCreado}
        proveedorId={formData.proveedor_id}
        nombreInicial={quickModal.nombreHint}
        precioCostoInicial={quickModal.precioHint}
      />
    </div>
  );
};

export default CompraForm;
